import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, CheckCircle2 } from "lucide-react";

export default function Support() {
  const { t, user } = useApp();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", subject: "", message: "", category: "general" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support/tickets", form);
      setSent(true);
      toast.success(t("support.sent"));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (sent) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="w-10 h-10 text-green-600" /></div>
      <h1 className="font-display text-4xl font-bold mb-3">{t("support.thankYouTitle")}</h1>
      <p className="text-muted-foreground mb-6">{t("support.thankYouMsg")}</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full bg-[#005C7A]/10 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-[#005C7A]" /></div>
        <div>
          <div className="overline text-[#E27B58]">{t("support.badge")}</div>
          <h1 className="font-display text-4xl font-bold">{t("support.title")}</h1>
        </div>
      </div>
      <p className="text-muted-foreground mb-8">{t("support.subtitle")}</p>
      <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-8 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>{t("support.name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="support-name" /></div>
          <div><Label>{t("support.email")}</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-email" /></div>
        </div>
        <div>
          <Label>{t("support.category")}</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="support-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">{t("support.cat.general")}</SelectItem>
              <SelectItem value="booking">{t("support.cat.booking")}</SelectItem>
              <SelectItem value="payment">{t("support.cat.payment")}</SelectItem>
              <SelectItem value="technical">{t("support.cat.technical")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("support.subject")}</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-subject" /></div>
        <div><Label>{t("support.message")}</Label><Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message" /></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] h-12" data-testid="support-submit">
          {busy ? "..." : t("support.submit")}
        </Button>
      </form>
    </div>
  );
}
